// QUDT dimension vectors look like `qkdv:A0E1L-3I0M0H0T0D0` — letter codes
// for each base quantity followed by signed integer exponents. We map them
// to the SI-style display ("M L⁻¹ T⁻²") used in the tooltip.
//
// Letter → display symbol mapping per QUDT's QuantityKindDimensionVector:
//   A → N   amount of substance (mol)
//   E → I   electric current (A)        — SI letter is I
//   L → L   length (m)
//   I → J   luminous intensity (cd)     — SI letter is J
//   M → M   mass (kg)
//   H → Θ   thermodynamic temperature   — SI letter is theta (Θ)
//   T → T   time (s)
//   D → ·   dimensionless flag (skipped from display)

interface BaseQty {
  qudt: string;
  display: string;
  /** Lower = printed earlier. Mirrors the SI convention M, L, T, I, Θ, N, J. */
  order: number;
}

const BASES: BaseQty[] = [
  { qudt: "M", display: "M", order: 0 },
  { qudt: "L", display: "L", order: 1 },
  { qudt: "T", display: "T", order: 2 },
  { qudt: "E", display: "I", order: 3 },
  { qudt: "H", display: "Θ", order: 4 },
  { qudt: "A", display: "N", order: 5 },
  { qudt: "I", display: "J", order: 6 },
];

const SUPER: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "-": "⁻",
  "+": "⁺",
};
function toSuper(n: number): string {
  if (n === 1) return "";
  let out = "";
  for (const ch of n.toString()) out += SUPER[ch] ?? ch;
  return out;
}

/**
 * Returns a display string like `"M L⁻¹ T⁻²"`, or `null` if the input is
 * empty, the format is unrecognized, or every exponent is zero (pure
 * dimensionless — `D1` only).
 *
 * Tolerant: ignores an optional `qkdv:` prefix and any whitespace.
 */
export function parseDimensionVector(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const body = raw.replace(/^qkdv:/, "").replace(/\s+/g, "");
  if (!body) return null;

  const re = /([A-Z])(-?\d+)/g;
  const found = new Map<string, number>();
  let m: RegExpExecArray | null;
  let matchedAny = false;
  while ((m = re.exec(body)) !== null) {
    matchedAny = true;
    found.set(m[1], parseInt(m[2], 10));
  }
  if (!matchedAny) return null;

  const parts: { order: number; text: string }[] = [];
  for (const base of BASES) {
    const exp = found.get(base.qudt);
    if (exp === undefined || exp === 0) continue;
    parts.push({ order: base.order, text: base.display + toSuper(exp) });
  }
  if (parts.length === 0) return null;
  parts.sort((a, b) => a.order - b.order);
  return parts.map((p) => p.text).join(" ");
}
